/**
 * Notifications + preferences + consent + dose-reminder API (P-13/P-14/P-18, CP-3).
 * Inbox is read + read-receipts; preferences are per-category channel toggles.
 */

import { apiFetch } from "./client";

export interface Notification {
  id: string;
  event_code: string;
  category: string;
  title_key: string;
  body_key: string;
  /** The English text the API resolved for title_key / body_key (ACCT-11). */
  title: string;
  body: string;
  deep_link: string | null;
  payload: Record<string, unknown>;
  delivered_via: string[];
  read_at: string | null;
  created_at: string;
}

export interface PaginatedNotifications {
  data: Notification[];
  total: number;
  unread: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

export const listNotifications = (page = 1, per_page = 25): Promise<PaginatedNotifications> =>
  apiFetch(`/api/v1/me/notifications?page=${page}&per_page=${per_page}`);

export const unreadCount = (): Promise<{ unread: number }> =>
  apiFetch("/api/v1/me/notifications/unread-count");

export const markRead = (id: string): Promise<unknown> =>
  apiFetch(`/api/v1/me/notifications/${id}/read`, { method: "POST" });

export const markAllRead = (): Promise<{ updated: number }> =>
  apiFetch("/api/v1/me/notifications/read-all", { method: "POST" });

// --- Preferences (CP-3) ---

export interface NotificationPreference {
  category: string;
  push_enabled: boolean;
  sms_enabled: boolean;
  inbox_enabled: boolean;
}

export const listPreferences = (): Promise<NotificationPreference[]> =>
  apiFetch("/api/v1/me/notification-preferences");

/** One category's channels to change — only those sent change (ACCT-16), so two quick toggles
 * can't undo each other from a stale copy of the row. */
export type NotificationPreferenceChange = Pick<NotificationPreference, "category"> &
  Partial<Omit<NotificationPreference, "category">>;

export const patchPreference = (body: NotificationPreferenceChange): Promise<NotificationPreference> =>
  apiFetch("/api/v1/me/notification-preferences", { method: "PATCH", body });

// --- Consent (P-18) ---

export interface ConsentStatus {
  consent_type: string;
  required_version: string | null;
  current_version: string | null;
  granted: boolean;
  needs_action: boolean;
}

export const listConsents = (): Promise<ConsentStatus[]> => apiFetch("/api/v1/me/consents");

export const grantConsent = (consent_type: string): Promise<ConsentStatus> =>
  apiFetch("/api/v1/me/consents", { method: "POST", body: { consent_type } });

/** Switch off an optional consent (ACCT-04, D-10): pharmacy sharing or transactional messages.
 * Required ones answer 409 CONSENT_NOT_WITHDRAWABLE; the government ID needs a code (the app). */
export const revokeConsent = (consent_type: string): Promise<ConsentStatus> =>
  apiFetch(`/api/v1/me/consents/${encodeURIComponent(consent_type)}/revoke`, { method: "POST" });

/** Several consents at once — those ticked on the consent screen after sign-in (ACCT-03). All or
 * none; returns the full status list, like listConsents. */
export const grantConsents = (
  consents: { consent_type: string; consent_version?: string | null }[],
): Promise<ConsentStatus[]> => apiFetch("/api/v1/me/consents/batch", { method: "POST", body: { consents } });

// --- Dose reminders (P-13) ---

export interface DoseReminderSettings {
  prescription_id: string;
  enabled: boolean;
  morning_hour: number | null;
  afternoon_hour: number | null;
  night_hour: number | null;
  reminder_count: number;
}

export const getDoseReminders = (rxId: string): Promise<DoseReminderSettings> =>
  apiFetch(`/api/v1/me/prescriptions/${rxId}/dose-reminders`);

export const setDoseReminders = (
  rxId: string,
  body: { enabled: boolean; morning_hour?: number | null; afternoon_hour?: number | null; night_hour?: number | null },
): Promise<DoseReminderSettings> =>
  apiFetch(`/api/v1/me/prescriptions/${rxId}/dose-reminders`, { method: "PUT", body });
